import React, { useState, useEffect } from "react";
// Make sure this path is correct based on where Navbar.jsx is located!
import Logo from "./Images/logo1.png";

// Your live website URL — used to build absolute URLs for Google's sitelinks schema
const SITE_URL = "https://www.anabel.in";

const NAV_LINKS = [
  { label: "ಮುಖಪುಟ / Home", schemaName: "Home", path: "/" },
  { label: "Courses", schemaName: "Courses", path: "/courses" },
  { label: "Gallery", schemaName: "Gallery", path: "/gallery" },
  { label: "About Us", schemaName: "About Us", path: "/about" },
  { label: "Contact", schemaName: "Contact", path: "/contact" }
];

const Navbar = () => {
  // State to manage mobile menu toggle
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Inject JSON-LD for Google Sitelinks once on mount, not on every render.
  // Using a standalone SiteNavigationElement array is the pattern Google's
  // own documentation and parsers recognize — mixing it inside an ItemList
  // of ListItems (the previous approach) is a non-standard pattern that
  // some validators will flag or simply ignore.
  useEffect(() => {
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "SiteNavigationElement",
      "name": NAV_LINKS.map((link) => link.schemaName),
      "url": NAV_LINKS.map((link) => `${SITE_URL}${link.path}`)
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "site-navigation-schema";
    script.text = JSON.stringify(structuredData);

    // Avoid duplicate injection if Navbar re-mounts (e.g. route changes)
    const existing = document.getElementById("site-navigation-schema");
    if (existing) existing.remove();

    document.head.appendChild(script);

    return () => {
      const el = document.getElementById("site-navigation-schema");
      if (el) el.remove();
    };
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <>
      {/* SEO/Accessibility Step: Skip to main content link (hidden until focused) */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-[1000] focus:bg-[#d11210] focus:text-white focus:px-4 focus:py-2 focus:top-0 focus:left-0"
      >
        Skip to main content
      </a>

      <nav
        aria-label="Primary Site Navigation"
        itemScope
        itemType="https://schema.org/SiteNavigationElement"
        className="bg-white border-b-[3px] border-[#1800ad] px-4 md:px-6 flex items-center justify-between sticky top-0 z-[999] shadow-[0_2px_15px_rgba(24,0,173,0.08)] h-[70px] md:h-[80px]"
      >

        <div className="flex items-center gap-2.5">
          {/* Logo Link to Home */}
          <a href="/" title="AnaBel Institute Homepage" className="flex items-center" aria-label="Go to AnaBel Institute Homepage">
            <img
              src={Logo}
              alt="AnaBel Institute - Quality Teacher Education"
              width="70"
              height="70"
              className="object-contain w-[56px] h-[56px] md:w-[70px] md:h-[70px] md:ml-10"
              fetchPriority="high" // Tells Google this image is critical for the initial load
            />
          </a>
        </div>

        {/* Desktop Navigation */}
        <ul className="hidden md:flex items-center gap-7 list-none m-0 p-0" role="menubar">
          {NAV_LINKS.map(link => (
            <li key={link.label} role="none">
              <a
                role="menuitem"
                href={link.path}
                itemProp="url"
                title={`Navigate to ${link.schemaName}`}
                className="text-[13px] font-bold text-[#000000] tracking-[0.03em] no-underline transition-colors duration-200 hover:text-[#d11210] focus:text-[#d11210] focus:outline-none focus:ring-2 focus:ring-[#d11210] focus:ring-offset-4 rounded-sm"
              >
                <span itemProp="name">{link.label}</span>
              </a>
            </li>
          ))}
        </ul>

        {/* Right Side Buttons (Apply + Hamburger) */}
        <div className="flex items-center gap-3">
          {/* Apply Button */}
          <a
            href="https://forms.gle/KiAg5XBjbgeD4FD98"
            title="Apply for Admission at AnaBel Institute"
            className="inline-flex items-center justify-center bg-[#d11210] hover:bg-[#a30e0c] text-white no-underline border-none rounded-lg px-4 py-2 md:px-5 md:py-[9px] text-[13px] md:text-[14px] font-bold cursor-pointer font-['Hind',sans-serif] transition-all duration-200 hover:scale-[1.02] focus:ring-2 focus:ring-offset-2 focus:ring-[#d11210]"
          >
            <span aria-hidden="true" className="mr-1">📋</span> Apply
          </a>

          {/* Hamburger Menu Button (Mobile Only) */}
          <button
            type="button"
            className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-[#1800ad] hover:text-[#d11210] hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#d11210]"
            aria-controls="mobile-menu"
            aria-expanded={isMobileMenuOpen}
            onClick={toggleMobileMenu}
          >
            <span className="sr-only">Open main menu</span>
            {/* Icon switches between Hamburger and X based on state */}
            {!isMobileMenuOpen ? (
              <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            ) : (
              <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile Navigation Dropdown */}
        <div
          id="mobile-menu"
          className={`${isMobileMenuOpen ? "block" : "hidden"} md:hidden absolute top-[70px] left-0 w-full bg-white border-b-[3px] border-[#1800ad] shadow-lg`}
        >
          <ul className="flex flex-col list-none m-0 p-0" role="menu">
            {NAV_LINKS.map((link) => (
              <li key={link.label} role="none" className="border-t border-gray-100">
                <a
                  role="menuitem"
                  href={link.path}
                  itemProp="url"
                  title={`Navigate to ${link.schemaName}`}
                  onClick={() => setIsMobileMenuOpen(false)} // Close menu when link is clicked
                  className="block px-6 py-4 text-[14px] font-bold text-[#000000] tracking-[0.03em] no-underline hover:bg-gray-50 hover:text-[#d11210] focus:text-[#d11210] focus:bg-gray-50 focus:outline-none"
                >
                  <span itemProp="name">{link.label}</span>
                </a>
              </li>
            ))}
          </ul>
        </div>

      </nav>
    </>
  );
};

export default Navbar;